import {
    type BranchOpener,
    CMMBranch,
    type MultiSelectOption,
    resolveInfoCards,
    WidgetType,
} from '@fruitful/types';

import type { ChatMessage } from '../types/ChatTypes';

/**
 * The onboarding conversation opens with a static welcome screen whose footer is a
 * branch-preference multi-select. The member's pick becomes the conversation's
 * first message — a `[multi-select-confirmed]` payload on this response key. Shared
 * so the member app's welcome screen and the QA simulation open the chat the same
 * way.
 */
export const CONVERSATIONAL_MM_WELCOME_RESPONSE_KEY = 'memberNotes';

type EnvAvailability = 'all' | 'production' | 'non-production';

const ALL_BRANCH_OPTIONS: Array<
    MultiSelectOption & { availability: EnvAvailability }
> = [
    {
        id: 'create-money-system-v2',
        label: 'Create a money system',
        availability: 'all',
    },
    // Retained in code (and QA sims) but hidden from members: not offered in
    // production. goals-debt was removed entirely; this is the single remaining
    // non-member branch.
    {
        id: 'start-investing',
        label: 'Optimize my investments',
        availability: 'non-production',
    },
];

/**
 * Returns the welcome-screen branch options filtered for the current environment.
 * To add a new experimental branch, add it with `'non-production'`. To promote it
 * to production, change it to `'all'` (or `'production'` to remove the old one).
 */
export function getWelcomeBranchOptions(
    isProduction: boolean,
): MultiSelectOption[] {
    return ALL_BRANCH_OPTIONS.filter(
        (o) =>
            o.availability === 'all' ||
            (isProduction
                ? o.availability === 'production'
                : o.availability === 'non-production'),
    ).map(({ availability: _, ...option }) => option);
}

/**
 * Deterministic mapping from welcome multi-select option IDs to CMM branches.
 * Used by the client to resolve the branch without LLM reasoning.
 */
export const WELCOME_OPTION_TO_BRANCH: Record<string, CMMBranch> = {
    'create-money-system-v2': CMMBranch.GoalsSystem,
    'start-investing': CMMBranch.StartInvesting,
};

/**
 * Resolves a welcome option ID to a branch. Returns null for free-text-only
 * submissions that need agent reasoning.
 */
export function resolveWelcomeBranch(
    selectedOptionId: string | undefined,
): CMMBranch | null {
    if (selectedOptionId == null) return null;
    return WELCOME_OPTION_TO_BRANCH[selectedOptionId] ?? null;
}

/** @deprecated Use getWelcomeBranchOptions(isProduction) for env-filtered list */
export const CONVERSATIONAL_MM_WELCOME_BRANCH_OPTIONS: MultiSelectOption[] =
    ALL_BRANCH_OPTIONS.map(({ availability: _, ...option }) => option);

/**
 * The goals-system branch id. Monopath: every member-facing welcome-menu pick
 * (and any free-text answer) commits this one branch — the picked option rides
 * along only as `welcomeOptionId` + member-notes context (it does NOT branch).
 * Shared so the member app's welcome menu and the QA simulation resolve the
 * first question to the same branch.
 */
export const GOALS_SYSTEM_WELCOME_OPTION_ID = 'create-money-system-v2';

/**
 * The member-facing "Where do you want to start?" first-question options — what
 * the member actually taps on the welcome screen (see the app's
 * `WelcomeStartMenu`). Every option monopaths to `GOALS_SYSTEM_WELCOME_OPTION_ID`;
 * the picked id is only carried through as `welcomeOptionId` context. Shared so
 * the QA simulation exercises the exact same question the member sees.
 */
export const CONVERSATIONAL_MM_WELCOME_MENU_OPTIONS: MultiSelectOption[] = [
    { id: 'get-out-of-debt', label: 'Get out of debt' },
    {
        id: 'create-budget-money-system',
        label: 'Create a budget or money system',
    },
    { id: 'make-progress-goals', label: 'Make progress toward my goals' },
    { id: 'optimize-investments', label: 'Optimize my investments' },
    { id: 'not-sure', label: 'I\u2019m not sure yet' },
];

/**
 * Default first line of the goals-system partner-question opener, used when a
 * selection has no bespoke copy yet (and for free-text / unmatched answers).
 *
 * The per-selection overrides live in the member app next to the welcome menu
 * options: `WELCOME_OPENER_INTRO_BY_OPTION` in `WelcomeStartMenu.tsx`.
 */
export const DEFAULT_WELCOME_OPENER_INTRO =
    "You're in the right place. We've helped thousands of people automate their finances and eliminate money stress.";

/**
 * Pre-rendered branch openers keyed by welcome-option ID. When present, the
 * app renders the opener instantly on branch selection instead of waiting for
 * the LLM's first response. Add an entry here to make a branch's opener
 * deterministic and zero-latency.
 */
const BRANCH_OPENERS: Partial<Record<string, BranchOpener>> = {
    'create-money-system-v2': {
        text: DEFAULT_WELCOME_OPENER_INTRO,
        // The welcome screen already shows the "just a few quick questions"
        // intro before the member picks, so the opener goes straight to the
        // partner question to avoid repeating it.
        slides: [],
        transitionText: 'First, are you planning your finances with a partner?',
    },
    'start-investing': {
        text: "You're in the right place. We've helped thousands of people build a system to optimize their investments.",
        slides: [],
        transitionText:
            "Just a few quick questions, and then we'll handle the rest. Are we planning for just you, or for you and a partner?",
    },
};

export function getBranchOpener(branchId: string): BranchOpener | undefined {
    return BRANCH_OPENERS[branchId];
}

/**
 * Stable ID prefix for synthetic opener messages so they can be identified
 * during restore and never collide with real Mastra message IDs.
 */
const SYNTHETIC_OPENER_ID_PREFIX = 'synthetic-opener-';

export function isSyntheticMessage(message: ChatMessage): boolean {
    return message.id.startsWith(SYNTHETIC_OPENER_ID_PREFIX);
}

/**
 * Build a synthetic assistant `ChatMessage` from a `BranchOpener` config.
 *
 * The resulting message has content parts [text, InfoCards widget, text] and
 * renders through the standard `SequentialAssistantContent` pipeline — no
 * custom component needed. The typewriter animation, sequential reveal, and
 * logo icon all come for free from the existing renderer.
 */
export function buildSyntheticOpenerMessage(
    branchId: string,
    opener: BranchOpener,
    introTextOverride?: string,
): ChatMessage {
    const content: ChatMessage['content'] = [
        { type: 'text', text: introTextOverride ?? opener.text },
    ];

    if (opener.slides.length > 0) {
        const cards = resolveInfoCards(opener.slides);
        content.push({
            type: 'ui',
            widget: WidgetType.InfoCards,
            props: { cards } as unknown as Record<string, unknown>,
        });
    }

    content.push({ type: 'text', text: opener.transitionText });

    return {
        id: `${SYNTHETIC_OPENER_ID_PREFIX}${branchId}`,
        role: 'assistant',
        content,
        createdAt: new Date(),
    };
}
